/**
 * @file SidebarUserMenu.tsx
 * @description Блок пользователя внизу тёмной боковой панели кабинета
 * платформы (phase19.29, второй проход — решение пользователя: из верхней
 * панели в низ боковой).
 *
 * Строка «аватар + имя + роль» — одна кнопка высотой ≥ 48px («Меню
 * пользователя»), открывает меню вверх. В меню — всё, что раньше было в
 * блоке пользователя шапки (`AppHeader`):
 *  - кто я и где я (имя + кабинет/роль, как в `CurrentIdentityWidget`);
 *  - переход в другой кабинет — список контекстов, как в `ContextSwitcher`
 *    (раньше на телефоне был пункт «Сменить контекст» → /select-context,
 *    теперь список один и тот же на всех ширинах);
 *  - «Выйти» — последним пунктом, после линии.
 * На телефоне блок внизу шторки меню (тот же `AppShell.SidebarFooter`).
 * phase19.31: длинные имя и роль переносятся, а не обрезаются многоточием.
 */

import { ChevronsUpDown, LogOut } from "lucide-react";
import { useNavigate } from "react-router";

import { formatContextLabel } from "../auth/format";
import { useAuth } from "../auth/hooks";
import { useContextSwitch } from "../hooks/useContextSwitch";
import { Avatar } from "./ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

export function SidebarUserMenu() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const onPick = useContextSwitch();

  if (!me) return null;

  const displayName = me.user.displayName ?? me.user.email;
  const roleLabel = me.activeContext?.role.displayName ?? formatContextLabel(me.activeContext);
  const contextLabel = formatContextLabel(me.activeContext);

  async function onLogout() {
    await logout();
    navigate("/login", { replace: true });
  }

  return (
    // Тонкая линия над блоком — на всю ширину панели: подвал панели без
    // своих отступов (`AppShell.SidebarFooter flush`), поля задаёт блок.
    <div className="border-t border-sidebar-border px-2 py-2">
      <DropdownMenu>
        <DropdownMenuTrigger
          render={(props) => (
            <button
              {...props}
              type="button"
              // Доступное имя включает видимый текст (имя пользователя).
              aria-label={`Меню пользователя: ${displayName}`}
              data-testid="sidebar-user-menu"
              className="flex min-h-12 w-full items-center gap-3 rounded-md px-2 py-2 text-left outline-none transition-colors hover:bg-sidebar-hover focus-visible:ring-2 focus-visible:ring-sidebar-ring"
            >
              <Avatar name={displayName} size={32} />
              <span className="flex min-w-0 flex-1 flex-col leading-tight">
                <span className="break-words text-sm font-semibold text-sidebar-foreground">
                  {displayName}
                </span>
                <span title={contextLabel} className="break-words text-xs text-sidebar-muted">
                  {roleLabel}
                </span>
              </span>
              <ChevronsUpDown className="size-4 shrink-0 text-sidebar-muted" aria-hidden />
            </button>
          )}
        />
        <DropdownMenuContent side="top" align="start" sideOffset={8} className="min-w-64">
          <DropdownMenuLabel className="normal-case tracking-normal">
            <div className="truncate text-sm font-medium text-on-surface">{displayName}</div>
            <div className="truncate text-xs font-normal text-on-surface-variant">{contextLabel}</div>
          </DropdownMenuLabel>
          {/* Во время входа под сотрудником — только его кабинет (SC42). */}
          {me.contexts.length > 1 && !me.impersonation ? (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="normal-case tracking-normal text-xs font-normal text-on-surface-variant">
                Перейти в кабинет
              </DropdownMenuLabel>
              {me.contexts.map((ctx) => (
                <DropdownMenuItem
                  key={ctx.id}
                  disabled={ctx.id === me.activeContext?.id}
                  onClick={() => void onPick(ctx)}
                  className="min-h-12 flex-col items-start gap-0.5 py-2"
                >
                  <span className="text-sm font-medium text-on-surface">{formatContextLabel(ctx)}</span>
                  <span className="text-xs text-on-surface-variant">{ctx.role.displayName}</span>
                </DropdownMenuItem>
              ))}
            </>
          ) : null}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => void onLogout()} className="min-h-12">
            <LogOut className="size-4" />
            Выйти
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
